/**
 * The Vanes' runner (Wilf's book, docs/TRADE-OFFLINE.md; research R14): a claret Haulmaster doing the round of
 * the Vanes' places — The Velvet Lamp, the Brickmakers Arms, Bonded Warehouse No. 3 — when Lenny says it's out.
 * It drives each leg with the race rivals' driver (src/activities/rivalDriver.ts: pure pursuit, brakes for
 * traffic) and stops `TRADE.runnerDwellS` seconds at each door: the moment to pull the driver out. Once you are
 * in it, it is yours to get to a lockup; the trade system opens the panels there.
 */
import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import { RivalDriver } from '../activities/rivalDriver';
import { kerbStop } from '../activities/roadLine';
import { planRoute, type Waypoint } from '../missions/autopilot';
import type { Vehicle } from '../vehicles/types';
import { RUNNER_PAINT, RUNNER_SPEC, RUNNER_STOPS, TRADE } from './tradeData';

export const RUNNER_TAG = 'trade:runner';
/** Cruise speed (m/s) and corner-cap multiplier: a loaded van in no hurry. */
const CRUISE = 12;
const CORNER = 0.85;
/** It never appears closer than this to you. */
const SPAWN_AWAY = 80;

export class Runner {
  /** You are at the wheel (pulled the driver out). */
  hijacked = false;
  /** Index of the leg being driven (stop `leg` → stop `leg + 1`). */
  leg: number;
  /** Seconds left at the current stop (> 0 while parked at a door). */
  dwell = 0;
  private driver: RivalDriver;

  private constructor(
    private readonly ctx: GameContext,
    readonly van: Vehicle,
    private readonly legs: Waypoint[][],
    start: number,
  ) {
    this.leg = start;
    this.dwell = TRADE.runnerDwellS;
    this.driver = new RivalDriver(ctx, van, legs[start]!, CRUISE, CORNER);
  }

  /** Put the van on the road at the first stop far enough from you; null when there is no room for it. */
  static spawn(ctx: GameContext): Runner | null {
    const layout = ctx.services.tryGet('layout');
    const vehicles = ctx.services.tryGet('vehicles');
    if (!layout || !vehicles) return null;
    const stops: V2[] = [];
    for (const id of RUNNER_STOPS) {
      const poi = layout.pois.find((p) => p.id === id);
      if (!poi) return null;
      stops.push(kerbStop(layout, poi.pos));
    }
    const legs = stops.map((s, i) => planRoute(layout, s, stops[(i + 1) % stops.length]!));
    if (legs.some((l) => l.length < 2)) return null;
    const me = ctx.services.get('player').pos;
    const start = stops.findIndex((s) => distance(s, me) >= SPAWN_AWAY);
    if (start < 0) return null;
    const at = stops[start]!;
    const next = legs[start]![1]!;
    const player = ctx.services.get('player');
    for (const q of vehicles.query(at, 4.5)) if (q !== player.vehicle && !q.isPolice && !q.tags.has('mission')) vehicles.despawn(q);
    const van = vehicles.spawn(RUNNER_SPEC, at, {
      heading: Math.atan2(next.x - at.x, next.z - at.z),
      controller: 'ai',
      occupant: { kind: 'ped', pedId: null },
      paint: RUNNER_PAINT,
      tags: [RUNNER_TAG],
    });
    return new Runner(ctx, van, legs, start);
  }

  /** Still a van with somebody of theirs at the wheel. */
  get driving(): boolean {
    const v = this.van;
    return !v.destroyed && v.controller === 'ai' && v.occupant?.kind === 'ped';
  }

  /** The van's next stop (where to cut it off). */
  get nextStop(): V2 | null {
    const l = this.legs[this.leg];
    return l ? l[l.length - 1]! : null;
  }

  /** Drive one fixed step (order band 200–299, before the vehicles step). */
  drive(dt: number): void {
    if (this.hijacked || !this.driving) return;
    const c = this.van.controls;
    if (this.dwell > 0) {
      this.dwell -= dt;
      c.throttle = this.van.speed > 0.3 ? -0.6 : 0;
      c.steer = 0;
      c.handbrake = this.van.speed < 0.5;
      if (this.dwell <= 0) {
        this.leg = (this.leg + 1) % this.legs.length;
        this.driver = new RivalDriver(this.ctx, this.van, this.legs[this.leg]!, CRUISE, CORNER);
      }
      return;
    }
    this.driver.drive(dt);
    if (this.driver.done && this.van.speed < 0.8) this.dwell = TRADE.runnerDwellS;
  }

  /** Out of your sight (to take it off the road when the tip runs out). */
  unseen(): boolean {
    const p = this.ctx.services.get('player').pos;
    const d = distance(p, this.van.pos);
    return d > 90 || (d > 45 && !this.ctx.physics.lineOfSight(p, this.van.pos));
  }
}
